import { isEqual, filter, first, last } from 'lodash-es';
import type { PassengersData } from '@aviata/booking-data';
import type {
  StructuredAncillaries,
  PassengerAncillaryState,
  PassengersAncillaryState,
} from '@aviata/ancillary-fns';
import type { flightEntity } from 'freedom-shared';

import type { PassengerWithBaggage } from '../types';

const getBaggagesPayload = (passenger: PassengerWithBaggage) => {
  const selectedBaggagesIds = passenger.flightsWithBaggages.map(flight => {
    const flightId = flight.id;
    const filteredBaggages = flight.availableBaggages.filter(
      baggage => baggage.isSelected,
    );
    return filteredBaggages.map(({ id }) => ({ id, flightId }));
  });

  const newState = selectedBaggagesIds.reduce(
    (prevState, nextFlightBaggages) => {
      if (nextFlightBaggages.length > 0) {
        // Need for keeping correct linking of baggage to flights
        prevState[nextFlightBaggages[0].flightId] = nextFlightBaggages[0];
      }
      return prevState;
    },
    {} as PassengerAncillaryState,
  );

  return { passengerKey: passenger.key, newState };
};

const getPassengersWithAvailableBaggages = (
  passengers: PassengersData,
  ancillariesList: Partial<StructuredAncillaries>,
  flights: flightEntity.types.MappedFlight[],
  ancillariesState?: PassengersAncillaryState,
) => {
  let hasSelectedBaggage = false;
  const getSegmentIdx = (flightIdx: number, segIdx: number) => {
    if (flightIdx === 0) {
      return segIdx;
    }
    return flightIdx * flights[flightIdx - 1].segments.length + segIdx;
  };

  const getAvailableBaggagesInFlight = (
    segmentIndices: number[],
    passengerIdx: number,
    passengerKey: string,
    flightIdx: number,
  ) => {
    const baggages = ancillariesList['airflow.ancillary.baggage'];
    if (!baggages) return [];

    const availableBaggages = filter(baggages[passengerIdx], baggage =>
      isEqual(baggage.segments, segmentIndices),
    );

    return availableBaggages.map(baggage => {
      const isSelected =
        ancillariesState?.[passengerKey]?.[flightIdx]?.id === baggage.id;

      if (isSelected) {
        hasSelectedBaggage = true;
      }
      return { ...baggage, isSelected };
    });
  };

  const getFlightsWithAvailableBaggages = (
    passIdx: number,
    passKey: string,
  ) => {
    const mappedFlights = flights.map((flight, flightIdx) => {
      const segmentIndices = flight.segments.map((_, segIdx) =>
        getSegmentIdx(flightIdx, segIdx),
      );

      return {
        ...flight,
        id: flightIdx,
        availableBaggages: getAvailableBaggagesInFlight(
          segmentIndices,
          passIdx,
          passKey,
          flightIdx,
        ),
        dep: first(flight.segments)?.dep.city,
        arr: last(flight.segments)?.arr.city,
      };
    });

    return mappedFlights.filter(
      flight => flight.availableBaggages?.length !== 0,
    );
  };

  const mappedPassengers = (
    Object.keys(passengers) as Array<keyof PassengersData>
  ).map((passKey, passIdx) => {
    return {
      ...passengers[passKey],
      id: passIdx,
      key: passKey,
      flightsWithBaggages: getFlightsWithAvailableBaggages(passIdx, passKey),
    };
  });

  return {
    passengers: mappedPassengers.filter(
      passenger => passenger.flightsWithBaggages.length !== 0,
    ),
    hasSelectedBaggage,
  };
};

export const baggagesLib = {
  getPassengersWithAvailableBaggages,
  getBaggagesPayload,
};
